import React, { useState, useEffect } from "react";
import { useAuthNOK } from "../context/AuthContextNok";
import { useHistory } from "react-router-dom";
import { useWebSocketNOK } from "../hooks/useWebSocketNOK";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
} from "recharts";
import { subDays, format } from "date-fns";

// Add keyframes for spinner animation
const spinnerKeyframes = `
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
`;

export const HomeNOK = () => {
  const [activeTab, setActiveTab] = useState("medical");
  const { nokUser, updateNokUser } = useAuthNOK();
  const history = useHistory();
  const [emergencyEnabled, setEmergencyEnabled] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [permissionsModalOpen, setPermissionsModalOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [nokList, setNokList] = useState([]);
  const [isEditingGeneral, setIsEditingGeneral] = useState(false);
  const [colibriData, setColibriData] = useState(null);
  const [isLoadingColibri, setIsLoadingColibri] = useState(false);
  const [isUsingMockData, setIsUsingMockData] = useState(false);
  const [generalSettings, setGeneralSettings] = useState({
    allowed: false,
    enableSMS: false,
    enableVoice: false,
  });
  const [selectedUserId, setSelectedUserId] = useState(null); // o null por defecto
  const [showChat, setShowChat] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [watchDisconnected, setWatchDisconnected] = useState(false);
  const selectedUser = nokList.find(nok => nok.userId.toString() === selectedUserId);


  const {
    socketEventBP,
    socketEventPulse,
    socketEventSPO2H,
    socketEventTemperature,
  } = useWebSocketNOK(selectedUserId) || {
    socketEventBP: {},
    socketEventPulse: {},
    socketEventSPO2H: {},
    socketEventTemperature: {},
  };

  useEffect(() => {
    console.log(
      "📢 Estado del WebSocket en HomeNOK:",
      socketEventBP,
      socketEventPulse,
      socketEventSPO2H,
      socketEventTemperature
    );
  }, [
    socketEventBP,
    socketEventPulse,
    socketEventSPO2H,
    socketEventTemperature,
  ]);
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (
        !socketEventBP &&
        !socketEventPulse &&
        !socketEventSPO2H &&
        !socketEventTemperature
      ) {
        setWatchDisconnected(true);
      } else {
        setWatchDisconnected(false);
      }
    }, 3000); // Esperamos 8 segundos sin datos

    return () => clearTimeout(timeout);
  }, [
    socketEventBP,
    socketEventPulse,
    socketEventSPO2H,
    socketEventTemperature,
  ]);

  const [healthData, setHealthData] = useState([
    {
      imageSrc: "/img/oxygen.png",
      value: "--%",
      label: "Oxygen Saturation",
      time: "--:--:--",
      color: "#1a9766",
    },
    {
      imageSrc: "/img/rate.png",
      value: "--",
      label: "Heart Rate",
      time: "--:--:--",
      color: "#1a9766",
    },
    {
      imageSrc: "/img/temperature.png",
      value: "--°C",
      label: "Temperature",
      time: "--:--:--",
      color: "#E67E22",
    },
    {
      imageSrc: "/img/bloodpressure.png",
      value: "-- / --",
      label: "Blood Pressure",
      time: "--:--:--",
      color: "#1a9766",
    },
  ]);
useEffect(() => {
  // Limpiar datos visuales al cambiar de usuario
  setHealthData([
    {
      imageSrc: "/img/oxygen.png",
      value: "--%",
      label: "Oxygen Saturation",
      time: "--:--:--",
      color: "#1a9766",
    },
    {
      imageSrc: "/img/rate.png",
      value: "--",
      label: "Heart Rate",
      time: "--:--:--",
      color: "#1a9766",
    },
    {
      imageSrc: "/img/temperature.png",
      value: "--°C",
      label: "Temperature",
      time: "--:--:--",
      color: "#E67E22",
    },
    {
      imageSrc: "/img/bloodpressure.png",
      value: "-- / --",
      label: "Blood Pressure",
      time: "--:--:--",
      color: "#1a9766",
    },
  ]);
}, [selectedUserId]);
  const generateFakeData = () => {
    const fakeData = {
      heartRate: [],
      bloodPressure: [],
      temperature: [],
      oxygenSaturation: [],
    };
    for (let i = 0; i < 14; i++) {
      const date = format(subDays(new Date(), i), "MMM d");

      fakeData.heartRate.push({
        time: date,
        value: Math.floor(Math.random() * (100 - 60) + 60), // 60-100 BPM
      });

      fakeData.bloodPressure.push({
        time: date,
        bpHigh: Math.floor(Math.random() * (140 - 110) + 110), // 110-140
        bpLow: Math.floor(Math.random() * (90 - 70) + 70), // 70-90
      });

      fakeData.temperature.push({
        time: date,
        value: (Math.random() * (37.5 - 36) + 36).toFixed(1), // 36-37.5°C
      });

      fakeData.oxygenSaturation.push({
        time: date,
        value: Math.floor(Math.random() * (100 - 92) + 92), // 92%-100%
      });
    }

    return {
      heartRate: fakeData.heartRate.reverse(),
      bloodPressure: fakeData.bloodPressure.reverse(),
      temperature: fakeData.temperature.reverse(),
      oxygenSaturation: fakeData.oxygenSaturation.reverse(),
    };
  };

  const [historicalData, setHistoricalData] = useState(generateFakeData());

  useEffect(() => {
    setHealthData((prev) => [
      {
        ...prev[0],
        value: socketEventSPO2H?.spo2hValue
          ? `${socketEventSPO2H.spo2hValue}%`
          : "--%",
        time: socketEventSPO2H?.timestampSeconds
          ? new Date(
              socketEventSPO2H.timestampSeconds * 1000
            ).toLocaleTimeString()
          : "--:--:--",
      },
      {
        ...prev[1],
        value: socketEventPulse?.pulse ? `${socketEventPulse.pulse}` : "--",
        time: socketEventPulse?.timestampSeconds
          ? new Date(
              socketEventPulse.timestampSeconds * 1000
            ).toLocaleTimeString()
          : "--:--:--",
      },
      {
        ...prev[2],
        value: socketEventTemperature?.temperature
          ? `${(socketEventTemperature.temperature / 10).toFixed(1)}°C`
          : "--°C",
        time: socketEventTemperature?.timestampSeconds
          ? new Date(
              socketEventTemperature.timestampSeconds * 1000
            ).toLocaleTimeString()
          : "--:--:--",
      },
      {
        ...prev[3],
        value:
          socketEventBP?.bpHigh && socketEventBP?.bpLow
            ? `${socketEventBP.bpHigh} / ${socketEventBP.bpLow}`
            : "-- / --",
        time: socketEventBP?.timestampSeconds
          ? new Date(socketEventBP.timestampSeconds * 1000).toLocaleTimeString()
          : "--:--:--",
      },
    ]);
  }, [
    socketEventBP,
    socketEventPulse,
    socketEventSPO2H,
    socketEventTemperature,
  ]);

  useEffect(() => {
    if (nokUser) {
      setEmergencyEnabled(nokUser.allowed === "1");
      setGeneralSettings({
        allowed: nokUser.allowed === "1",
        enableSMS: !!nokUser.enableSMS,
        enableVoice: !!nokUser.enableVoice,
      });
    }
  }, [nokUser]);

  useEffect(() => {
    // Add spinner animation styles to document
    const styleSheet = document.createElement("style");
    styleSheet.textContent = spinnerKeyframes;
    document.head.appendChild(styleSheet);

    return () => {
      document.head.removeChild(styleSheet);
    };
  }, []);

  const handleToggleEmergency = async () => {
    const newAllowedValue = emergencyEnabled ? "0" : "1";
    try {
      const response = await fetch(
        "https://vivesupportnokback-ctfcg7hfgtd6g0h2.canadacentral-01.azurewebsites.net/api/changeAllowed",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            auth_token: nokUser.token,
          },
          body: JSON.stringify({
            contactListId: nokUser.nokId,
            change: newAllowedValue,
          }),
        }
      );
      const result = await response.json();
      if (response.ok && result.win === 1) {
        setEmergencyEnabled(!emergencyEnabled);
        updateNokUser({ allowed: newAllowedValue });
      } else {
        console.error("Error updating allowed:", result.responseMessage);
      }
    } catch (error) {
      console.error("API error:", error);
    }
  };

  useEffect(() => {
    const fetchNokList = async () => {
      if (!nokUser?.token || !nokUser?.nokId) {
        console.error("❌ Token o nokId no disponible.");
        return;
      }

      try {
        const response = await fetch(
          "https://vivesupportnokback-ctfcg7hfgtd6g0h2.canadacentral-01.azurewebsites.net/api/listMessNok",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              auth_token: nokUser.token,
            },
            body: JSON.stringify({
              contactListId: nokUser.nokId,
            }),
          }
        );

        const result = await response.json();
        console.log("📦 NOK List API Response:", result);

        if (Array.isArray(result?.data)) {
          setNokList(result.data);
          if (!selectedUserId && result.data.length > 0) {
            setSelectedUserId(result.data[0].userId.toString());
          }
        } else {
          console.error("❌ La respuesta no contiene 'data' como array.");
        }
      } catch (error) {
        console.error("❌ Error fetching NOK list:", error);
      }
    };

    fetchNokList();
  }, [nokUser]);

  const handleDeleteNok = async () => {
    const contactListId = nokUser.nokId;

    if (!contactListId) {
      console.error("❌ Error: contactListId es inválido.");
      return;
    }

    console.log("📢 Eliminando contacto con ID:", contactListId);

    if (!nokUser?.token) {
      return console.error("❌ No token found. User must be logged in.");
    }

    if (!window.confirm("Are you sure you want to delete this contact?")) {
      return;
    }

    try {
      const response = await fetch(
        "https://vivesupportnokback-ctfcg7hfgtd6g0h2.canadacentral-01.azurewebsites.net/api/deleteContact",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            auth_token: nokUser.token,
          },
          body: JSON.stringify({ contactListId: contactListId.toString() }),
        }
      );

      const result = await response.json();
      console.log("📢 API Response:", result);

      if (response.ok && result.win === 1) {
        console.log("✅ Contacto eliminado correctamente.");
        alert("✅ Contact deleted successfully.");

        history.push("/");
      } else {
        console.error("❌ Error deleting contact:", result.responseMessage);
      }
    } catch (error) {
      console.error("❌ Error deleting contact:", error);
    }
  };

  const confirmDelete = () => {
    alert("Account deleted successfully.");
    setShowPopup(false);
  };

  const handleSaveGeneralSettings = async () => {
    try {
      const response = await fetch(
        "https://vivesupportnokback-ctfcg7hfgtd6g0h2.canadacentral-01.azurewebsites.net/api/changeAllowed",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            auth_token: nokUser.token,
          },
          body: JSON.stringify({
            contactListId: nokUser.nokId,
            allowed: generalSettings.allowed ? 1 : 0,
            enableSMS: generalSettings.enableSMS ? 1 : 0,
            enableVoice: generalSettings.enableVoice ? 1 : 0,
          }),
        }
      );

      const result = await response.json();
      if (response.ok && result.win === 1) {
        alert("✅ General settings updated");
        setIsEditingGeneral(false);
        updateNokUser({
          ...nokUser,
          allowed: generalSettings.allowed ? "1" : "0",
          enableSMS: generalSettings.enableSMS,
          enableVoice: generalSettings.enableVoice,
        });
      } else {
        alert("⚠ Error updating general settings");
      }
    } catch (error) {
      console.error("Error saving settings:", error);
      alert("⚠ Server error");
    }
  };

  console.log("📦 Datos en AuthContextNOK:", nokUser);

  const handleEditPermissions = (contact) => {
    setSelectedContact({
      ...contact,
      userId: contact.userId,
      allowed:
        contact.allowed === true ||
        contact.allowed === "1" ||
        contact.allowed === 1,
      enableSms:
        contact.enableSMS === true ||
        contact.enableSMS === "1" ||
        contact.enableSMS === 1,
      enableVoice:
        contact.enableVoice === true ||
        contact.enableVoice === "1" ||
        contact.enableVoice === 1,
    });
    setPermissionsModalOpen(true);
  };
  const handleDeleteContact = (contact) => {
    if (
      window.confirm(`Are you sure you want to delete ${contact.nameContact}?`)
    ) {
      setNokList((prevList) =>
        prevList.filter((c) => c.contactId !== contact.contactId)
      );
    }
  };

  const fetchColibriData = async () => {
    setIsLoadingColibri(true);
    setIsUsingMockData(false);
    try {
      console.log("Fetching CoLibri data...");
      const response = await fetch("/api/v2/metrics/user?userId=12", {
        method: "GET",
        headers: {
          Accept: "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      console.log("Response status:", response.status);
      const data = await response.json();
      console.log("CoLibri API Response:", data);
      setColibriData(data);
    } catch (error) {
      console.error("Error fetching CoLibri data:", error);
      console.error("Error details:", {
        message: error.message,
        stack: error.stack,
      });
      // Set a mock data for testing UI
      const mockData = {
        heartRateMetrics: {
          accumulator: {
            sum: 1980.0,
            sumSquares: 158390.0,
            count: 25,
            mean: 79.2,
            variance: 62.96,
            standardDeviation: 7.9347,
            min: 70.0,
            max: 108.0,
          },
          estRestHeartRate: 70.0,
          numberOfDeviations: 0,
        },
        spO2Metrics: {
          accumulator: {
            sum: 98.0,
            sumSquares: 9604.0,
            count: 1,
            mean: 98.0,
            variance: 0.0,
            standardDeviation: 0.0,
            min: 98.0,
            max: 98.0,
          },
          lowSpO2Count: 0,
          highSpO2Count: 0,
        },
        temperatureMetrics: {
          accumulator: {
            sum: 1108.7,
            sumSquares: 39656.55,
            count: 31,
            mean: 35.7645,
            variance: 0.1429,
            standardDeviation: 0.3781,
            min: 35.4,
            max: 36.2,
          },
        },
        bloodPressureMetrics: {
          currentCategory: "HIGH_NORMAL",
          detailedMeasurements: {
            OPTIMAL: { systolic: 112, diastolic: 76 },
            NORMAL: { systolic: 120, diastolic: 77 },
            HIGH_NORMAL: { systolic: 113, diastolic: 85 },
            GRADE1_HYPERTENSION: { systolic: 122, diastolic: 91 },
          },
          timesTracked: {
            OPTIMAL: 1745934404,
            NORMAL: 1745943027,
            HIGH_NORMAL: 1745943167,
            GRADE1_HYPERTENSION: 1745847204,
          },
          countsTracked: {
            OPTIMAL: 2,
            NORMAL: 15,
            HIGH_NORMAL: 8,
            GRADE1_HYPERTENSION: 1,
          },
        },
        overallHealthScore: 79.1036,
        healthState: "HEALTHY",
        lastHeartRate: 71.0,
        lastSpO2: 98.0,
        lastTemperature: 36.1,
        lastBpHigh: 113.0,
        lastBpLow: 85.0,
      };
      setColibriData(mockData);
      setIsUsingMockData(true);
    } finally {
      setIsLoadingColibri(false);
    }
  };

  const formatBloodPressureCategory = (category) => {
    return category
      .replace(/_/g, " ")
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  const analyzeVitals = async () => {
    setIsAnalyzing(true);
    setShowChat(true);
    setChatMessages([]); // Clear previous chat

    const vitalSigns = {
      heartRate: colibriData.lastHeartRate,
      bloodPressure: `${colibriData.lastBpHigh}/${colibriData.lastBpLow}`,
      temperature: colibriData.lastTemperature,
      spo2: colibriData.lastSpO2,
    };

    const message = `Heart rate: ${vitalSigns.heartRate}, Blood Pressure: ${vitalSigns.bloodPressure}, Temperature: ${vitalSigns.temperature}C, Spo2: ${vitalSigns.spo2}`;

    try {
      const response = await fetch(
        "https://colibri-lm-ollama-byhbggb0frheebas.norwayeast-01.azurewebsites.net/api/chat",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            model: "qwen2:0.5b",
            messages: [
              {
                role: "system",
                content:
                  "You are an AI specialist within analyzing vital signs.",
              },
              {
                role: "user",
                content: message,
              },
            ],
            stream: true,
          }),
        }
      );

      const reader = response.body.getReader();
      let fullResponse = "";
      let assistantStarted = false;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = new TextDecoder().decode(value);
        const lines = chunk.split("\n").filter((line) => line.trim());

        for (const line of lines) {
          try {
            const data = JSON.parse(line);
            if (data.message?.content) {
              fullResponse += data.message.content;
              if (!assistantStarted) {
                setChatMessages((prev) => [
                  ...prev,
                  { role: "assistant", content: fullResponse },
                ]);
                assistantStarted = true;
              } else {
                setChatMessages((prev) => {
                  const updated = [...prev];
                  updated[updated.length - 1] = {
                    ...updated[updated.length - 1],
                    content: fullResponse,
                  };
                  return updated;
                });
              }
            }
          } catch (e) {
            console.error("Error parsing JSON:", e);
          }
        }
      }
    } catch (error) {
      console.error("Error analyzing vitals:", error);
      setChatMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: "Sorry, there was an error analyzing the vital signs.",
        },
      ]);
    } finally {
      setIsAnalyzing(false);
    }
  };

  return (
    <div
      id="home-user"
      className="text-center"
      style={{
        backgroundColor: "#fff",
        padding: "40px",
        marginTop: "90px",
      }}
    >
      <div className="container">
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            marginBottom: "20px",
          }}
        >
          <button
            onClick={() => setActiveTab("medical")}
            style={getButtonStyle(activeTab === "medical")}
          >
            Medical Data
          </button>
          <button
            onClick={() => setActiveTab("settings")}
            style={getButtonStyle(activeTab === "settings")}
          >
            Settings
          </button>
          <button
            onClick={() => {
              setActiveTab("colibri");
              fetchColibriData();
            }}
            style={getButtonStyle(activeTab === "colibri")}
          >
            CoLibri
          </button>
          <button
            onClick={() => (window.location.href = "/")}
            style={getButtonStyle(false)}
          >
            Logout
          </button>
        </div>

        {activeTab === "medical" && (
          <>
            <div className="section-title">
              <h2>Health Monitoring</h2>
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: "20px",
              }}
            >
              <label
                htmlFor="userSelect"
                style={{
                  fontSize: "18px",
                  marginRight: "10px",
                  fontWeight: "bold",
                  color: "#333",
                }}
              >
                See data from:
              </label>
              <select
                id="userSelect"
                style={{
                  padding: "10px",
                  borderRadius: "5px",
                  border: "1px solid #ccc",
                  minWidth: "200px",
                  fontSize: "16px",
                }}
                value={selectedUserId}
                onChange={(e) => setSelectedUserId(e.target.value)}
              >
                {nokList.map((nok) => (
                  <option key={nok.userId} value={nok.userId}>
                    {nok.firstName}
                  </option>
                ))}
              </select>
            </div>
            {watchDisconnected && (
              <div
                style={{
                  backgroundColor: "#ffe6e6",
                  border: "1px solid #ff4d4f",
                  color: "#a94442",
                  padding: "15px",
                  borderRadius: "8px",
                  marginBottom: "20px",
                  fontWeight: "bold",
                }}
              >
                🚫 The watch <strong>{selectedUser?.firstName || "el usuario seleccionado"}</strong> is disconnected. You'll see information when the device reconnects.
              </div>
            )}

            <div className="row">
              {healthData.map((data, index) => (
                <div
                  key={index}
                  className="col-sm-6 col-md-6 col-lg-6"
                  style={{ padding: "15px" }}
                >
                  <div style={healthCardStyle}>
                    <img
                      src={data.imageSrc}
                      alt={data.label}
                      style={imageStyle}
                    />
                    <div>
                      <h2 style={valueStyle}>{data.value}</h2>
                      <p style={labelStyle}>{data.label}</p>
                      <small style={timeStyle}>Received: {data.time}</small>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {activeTab === "settings" && (
          <div className="section-title">
            <h2>Settings</h2>

            <div style={cardStyle}>
              <h3 style={{ color: "#1a9766" }}>User Information</h3>
              <p>
                <strong>Name:</strong> {nokUser?.nameContact || "N/A"}
              </p>
              <p>
                <strong>Phone:</strong> {nokUser?.phoneNumber || "N/A"}
              </p>
            </div>

            <div style={switchContainerStyle}>
              <span style={switchLabelStyle}>
                Disable/Enable Allowed in General:
              </span>
              <div
                style={switchWrapper}
                onClick={() =>
                  isEditingGeneral &&
                  setGeneralSettings((prev) => ({
                    ...prev,
                    allowed: !prev.allowed,
                  }))
                }
              >
                <div
                  style={{
                    ...switchSlider,
                    backgroundColor: generalSettings.allowed
                      ? "#1a9766"
                      : "#ccc",
                  }}
                >
                  <div
                    style={{
                      ...switchCircle,
                      left: generalSettings.allowed ? "26px" : "2px",
                    }}
                  ></div>
                </div>
              </div>
            </div>

            <div style={switchContainerStyle}>
              <span style={switchLabelStyle}>
                Disable/Enable SMS in General:
              </span>
              <div
                style={switchWrapper}
                onClick={() =>
                  isEditingGeneral &&
                  setGeneralSettings((prev) => ({
                    ...prev,
                    enableSMS: !prev.enableSMS,
                  }))
                }
              >
                <div
                  style={{
                    ...switchSlider,
                    backgroundColor: generalSettings.enableSMS
                      ? "#1a9766"
                      : "#ccc",
                  }}
                >
                  <div
                    style={{
                      ...switchCircle,
                      left: generalSettings.enableSMS ? "26px" : "2px",
                    }}
                  ></div>
                </div>
              </div>
            </div>

            <div style={switchContainerStyle}>
              <span style={switchLabelStyle}>
                Disable/Enable Calls in General:
              </span>
              <div
                style={switchWrapper}
                onClick={() =>
                  isEditingGeneral &&
                  setGeneralSettings((prev) => ({
                    ...prev,
                    enableVoice: !prev.enableVoice,
                  }))
                }
              >
                <div
                  style={{
                    ...switchSlider,
                    backgroundColor: generalSettings.enableVoice
                      ? "#1a9766"
                      : "#ccc",
                  }}
                >
                  <div
                    style={{
                      ...switchCircle,
                      left: generalSettings.enableVoice ? "26px" : "2px",
                    }}
                  ></div>
                </div>
              </div>
            </div>

            {isEditingGeneral ? (
              <button
                style={saveButtonStyle}
                onClick={handleSaveGeneralSettings}
              >
                Save
              </button>
            ) : (
              <button
                style={editButtonStyle}
                onClick={() => setIsEditingGeneral(true)}
              >
                Edit
              </button>
            )}
            <button onClick={handleDeleteNok} style={deleteButtonStyle}>
              Delete Account
            </button>
          </div>
        )}

        {activeTab === "colibri" && (
          <div className="section-title">
            <h2>CoLibri Health Metrics</h2>
            {isUsingMockData && (
              <div
                style={{
                  backgroundColor: "#fff3cd",
                  color: "#856404",
                  padding: "10px",
                  borderRadius: "5px",
                  marginBottom: "20px",
                  textAlign: "center",
                }}
              >
                ⚠️ Using mock data - API connection failed
              </div>
            )}
            {isLoadingColibri ? (
              <div style={loadingStyle}>
                <div style={spinnerStyle}></div>
                <p>Loading health metrics...</p>
              </div>
            ) : colibriData ? (
              <div style={metricsContainerStyle}>
                <div style={metricsCardStyle}>
                  <h3>Overall Health Score</h3>
                  <div style={scoreCircleStyle}>
                    <span style={scoreTextStyle}>
                      {colibriData.overallHealthScore.toFixed(1)}
                    </span>
                  </div>
                  <p style={healthStateStyle}>
                    Health State: {colibriData.healthState}
                  </p>
                  <button
                    onClick={analyzeVitals}
                    style={{
                      backgroundColor: "#1a9766",
                      color: "white",
                      padding: "10px 20px",
                      border: "none",
                      borderRadius: "5px",
                      cursor: "pointer",
                      marginTop: "20px",
                      fontSize: "16px",
                    }}
                    disabled={isAnalyzing}
                  >
                    {isAnalyzing ? "Analyzing..." : "Analyze Vitals"}
                  </button>
                </div>

                {showChat && (
                  <div
                    style={{
                      position: "fixed",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      width: "600px",
                      height: "650px",
                      backgroundColor: "white",
                      borderRadius: "16px",
                      boxShadow: "0 0 24px rgba(0,0,0,0.25)",
                      display: "flex",
                      flexDirection: "column",
                      zIndex: 1000,
                    }}
                  >
                    <div
                      style={{
                        padding: "20px",
                        backgroundColor: "#1a9766",
                        color: "white",
                        borderTopLeftRadius: "16px",
                        borderTopRightRadius: "16px",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <h3 style={{ margin: 0, fontSize: "1.5rem" }}>
                        Vital Signs Analysis
                      </h3>
                      <button
                        onClick={() => setShowChat(false)}
                        style={{
                          background: "none",
                          border: "none",
                          color: "white",
                          fontSize: "2rem",
                          cursor: "pointer",
                          lineHeight: 1,
                        }}
                      >
                        ×
                      </button>
                    </div>
                    <div
                      style={{
                        flex: 1,
                        overflowY: "auto",
                        padding: "24px",
                        display: "flex",
                        flexDirection: "column",
                        gap: "16px",
                        fontSize: "1.15rem",
                      }}
                    >
                      {chatMessages.map((msg, index) => (
                        <div
                          key={index}
                          style={{
                            backgroundColor:
                              msg.role === "assistant" ? "#f0f0f0" : "#e3f2fd",
                            padding: "14px",
                            borderRadius: "12px",
                            maxWidth: "90%",
                            alignSelf:
                              msg.role === "assistant"
                                ? "flex-start"
                                : "flex-end",
                            fontSize: "1.1rem",
                          }}
                        >
                          {msg.content}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div style={metricsGridStyle}>
                  <div style={metricCardStyle}>
                    <h4>Heart Rate</h4>
                    <p>Current: {colibriData.lastHeartRate} BPM</p>
                    <p>
                      Average:{" "}
                      {colibriData.heartRateMetrics.accumulator.mean.toFixed(1)}{" "}
                      BPM
                    </p>
                    <p>
                      Range: {colibriData.heartRateMetrics.accumulator.min} -{" "}
                      {colibriData.heartRateMetrics.accumulator.max} BPM
                    </p>
                  </div>

                  <div style={metricCardStyle}>
                    <h4>Blood Pressure</h4>
                    <p>
                      Current: {colibriData.lastBpHigh}/{colibriData.lastBpLow}{" "}
                      mmHg
                    </p>
                    <p>
                      Category:{" "}
                      {formatBloodPressureCategory(
                        colibriData.bloodPressureMetrics.currentCategory
                      )}
                    </p>
                  </div>

                  <div style={metricCardStyle}>
                    <h4>SpO2</h4>
                    <p>Current: {colibriData.lastSpO2}%</p>
                    <p>Average: {colibriData.spO2Metrics.accumulator.mean}%</p>
                  </div>

                  <div style={metricCardStyle}>
                    <h4>Temperature</h4>
                    <p>Current: {colibriData.lastTemperature}°C</p>
                    <p>
                      Average:{" "}
                      {colibriData.temperatureMetrics.accumulator.mean.toFixed(
                        1
                      )}
                      °C
                    </p>
                  </div>
                </div>

                <div style={chartContainerStyle}>
                  <h3>Blood Pressure Categories</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart
                      data={Object.entries(
                        colibriData.bloodPressureMetrics.countsTracked
                      ).map(([category, count]) => ({
                        category: formatBloodPressureCategory(category),
                        count,
                      }))}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="category" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="count" fill="#1a9766" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            ) : (
              <p>No data available</p>
            )}
          </div>
        )}

        <div style={{ marginTop: "40px" }}>
          <h3 style={{ color: "#1a9766" }}>Manage NOKs</h3>
          <table style={tableStyle}>
            <thead>
              <tr style={tableHeaderStyle}>
                <th style={thStyle}>Name</th>
                <th style={thStyle}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {nokList.map((contact) => (
                <tr key={contact.id} style={{ backgroundColor: "#f9f9f9" }}>
                  <td style={tdStyle}>{contact.firstName}</td>
                  <td style={tdStyle}>
                    <button
                      onClick={() => handleEditPermissions(contact)}
                      style={editButtonStyle}
                    >
                      Edit Permissions
                    </button>
                    <button
                      onClick={() => handleDeleteContact(contact)}
                      style={deleteButtonStyle}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {permissionsModalOpen && selectedContact && (
          <div style={popupStyle}>
            <div style={popupContentStyle}>
              <h3>Edit Permissions for {selectedContact.name}</h3>

              <div style={switchContainerStyle}>
                <span style={switchLabelStyle}>Active as NOK:</span>
                <div
                  style={switchWrapper}
                  onClick={() =>
                    setSelectedContact((prev) => ({
                      ...prev,
                      allowed: !prev.allowed,
                    }))
                  }
                >
                  <div
                    style={{
                      ...switchSlider,
                      backgroundColor: selectedContact.allowed
                        ? "#1a9766"
                        : "#ccc",
                    }}
                  >
                    <div
                      style={{
                        ...switchCircle,
                        left: selectedContact.allowed ? "26px" : "2px",
                      }}
                    ></div>
                  </div>
                </div>
              </div>

              <div style={switchContainerStyle}>
                <span style={switchLabelStyle}>Allow SMS:</span>
                <div
                  style={switchWrapper}
                  onClick={() =>
                    setSelectedContact((prev) => ({
                      ...prev,
                      enableSms: !prev.enableSms,
                    }))
                  }
                >
                  <div
                    style={{
                      ...switchSlider,
                      backgroundColor: selectedContact.enableSms
                        ? "#1a9766"
                        : "#ccc",
                    }}
                  >
                    <div
                      style={{
                        ...switchCircle,
                        left: selectedContact.enableSms ? "26px" : "2px",
                      }}
                    ></div>
                  </div>
                </div>
              </div>

              <div style={switchContainerStyle}>
                <span style={switchLabelStyle}>Allow Calls:</span>
                <div
                  style={switchWrapper}
                  onClick={() =>
                    setSelectedContact((prev) => ({
                      ...prev,
                      enableVoice: !prev.enableVoice,
                    }))
                  }
                >
                  <div
                    style={{
                      ...switchSlider,
                      backgroundColor: selectedContact.enableVoice
                        ? "#1a9766"
                        : "#ccc",
                    }}
                  >
                    <div
                      style={{
                        ...switchCircle,
                        left: selectedContact.enableVoice ? "26px" : "2px",
                      }}
                    ></div>
                  </div>
                </div>
              </div>

              <button
                onClick={async () => {
                  try {
                    const payload = {
                      userChange: selectedContact.userId,
                      allowed: selectedContact.allowed ? 1 : 0,
                      enableSMS: selectedContact.enableSms ? 1 : 0,
                      enableVoice: selectedContact.enableVoice ? 1 : 0,
                    };

                    console.log("📤 Body enviado a la API:", payload);

                    const response = await fetch(
                      "https://vivesupportnokback-ctfcg7hfgtd6g0h2.canadacentral-01.azurewebsites.net/api/changeAllowedUser",
                      {
                        method: "POST",
                        headers: {
                          "Content-Type": "application/json",
                          auth_token: nokUser.token,
                        },
                        body: JSON.stringify(payload),
                      }
                    );

                    const result = await response.json();
                    console.log("📡 Respuesta de cambio de permisos:", result);

                    if (response.ok && result.win === 1) {
                      alert("✅ Permissions updated successfully!");
                      setPermissionsModalOpen(false);
                    } else {
                      console.error(
                        "❌ Error actualizando permisos:",
                        result.responseMessage
                      );
                      alert("⚠ Failed to update permissions.");
                    }
                  } catch (error) {
                    console.error("❌ Error al hacer la solicitud:", error);
                    alert("⚠ An unexpected error occurred.");
                  }
                }}
                style={{ ...saveButtonStyle, marginTop: "20px" }}
              >
                Save
              </button>

              <button
                onClick={() => setPermissionsModalOpen(false)}
                style={{
                  ...deleteButtonStyle,
                  backgroundColor: "#777",
                  marginTop: "10px",
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
      {showPopup && (
        <div style={popupStyle}>
          <div style={popupContentStyle}>
            <h3>Are you sure?</h3>
            <p>This action cannot be undone.</p>
            <button
              onClick={confirmDelete}
              style={{ ...deleteButtonStyle, backgroundColor: "#e74c3c" }}
            >
              Confirm
            </button>
            <button
              onClick={() => setShowPopup(false)}
              style={{ ...deleteButtonStyle, backgroundColor: "#777" }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const deleteButtonStyle = {
  backgroundColor: "#e74c3c",
  color: "#fff",
  padding: "10px 20px",
  border: "none",
  borderRadius: "5px",
  cursor: "pointer",
  transition: "0.3s",
  fontSize: "16px",
  marginTop: "20px",
};
const popupStyle = {
  position: "fixed",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  backgroundColor: "rgba(0, 0, 0, 0.5)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1000,
};
const popupContentStyle = {
  backgroundColor: "#fff",
  padding: "20px",
  borderRadius: "10px",
  textAlign: "center",
  boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.2)",
};
const healthCardStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-evenly",
  backgroundColor: "#f5f5f5",
  padding: "15px",
  borderRadius: "10px",
  boxShadow: "0px 4px 6px rgba(0,0,0,0.1)",
};

const imageStyle = {
  width: "100px",
  height: "100px",
  objectFit: "contain",
  marginRight: "10px",
};

const valueStyle = {
  margin: "0",
  fontWeight: "bold",
  color: "#333",
  fontSize: "60px",
};

const labelStyle = { margin: "0", color: "#777", fontSize: "20px" };
const timeStyle = { color: "#aaa", fontSize: "20px" };

const getButtonStyle = (isActive) => ({
  padding: "10px 20px",
  margin: "0 5px",
  border: "none",
  borderRadius: "5px",
  cursor: "pointer",
  backgroundColor: isActive ? "#1a9766" : "#ddd",
  color: isActive ? "#fff" : "#333",
  fontSize: "16px",
  transition: "0.3s",
});

const cardStyle = {
  backgroundColor: "#f5f5f5",
  padding: "20px",
  borderRadius: "10px",
  boxShadow: "0px 4px 6px rgba(0,0,0,0.1)",
  textAlign: "center",
  margin: "20px auto",
  width: "80%",
};

const switchContainerStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginTop: "20px",
};

const switchLabelStyle = {
  fontSize: "18px",
  marginRight: "10px",
};

const switchWrapper = {
  width: "50px",
  height: "25px",
  borderRadius: "15px",
  position: "relative",
  cursor: "pointer",
};

const switchSlider = {
  width: "100%",
  height: "100%",
  borderRadius: "15px",
  position: "absolute",
  transition: "background-color 0.3s",
};

const switchCircle = {
  width: "20px",
  height: "20px",
  backgroundColor: "#fff",
  borderRadius: "50%",
  position: "absolute",
  top: "2px",
  transition: "left 0.3s",
};

const inputStyle = {
  padding: "10px",
  margin: "5px",
  borderRadius: "5px",
  border: "1px solid #ccc",
  width: "200px",
};

const addButtonStyle = {
  padding: "10px 20px",
  backgroundColor: "#1a9766",
  color: "#fff",
  border: "none",
  borderRadius: "5px",
  cursor: "pointer",
};

const editButtonStyle = {
  backgroundColor: "#3498DB",
  color: "#fff",
  padding: "10px 20px",
  border: "none",
  borderRadius: "5px",
  cursor: "pointer",
  marginRight: 10,
  fontSize: "16px",
};
const saveButtonStyle = {
  backgroundColor: "#2ECC71",
  margin: "5px",
  color: "#fff",
  padding: "10px 20px",
  border: "none",
  borderRadius: "5px",
  cursor: "pointer",
  fontSize: "16px",
};

const tableStyle = {
  width: "100%",
  borderCollapse: "collapse",
  borderRadius: "10px",
  overflow: "hidden",
  boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
};
const tableHeaderStyle = { backgroundColor: "#1a9766", color: "#fff" };
const thStyle = {
  padding: "12px",
  textAlign: "center",
  fontSize: "18px",
  fontWeight: "bold",
  borderBottom: "2px solid #ddd",
};
const tdStyle = {
  padding: "12px",
  textAlign: "center",
  fontSize: "16px",
  color: "#333",
  borderBottom: "1px solid #ddd",
};

const loadingStyle = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  height: "200px",
  width: "100%",
};

const spinnerStyle = {
  width: "50px",
  height: "50px",
  border: "5px solid #f3f3f3",
  borderTop: "5px solid #1a9766",
  borderRadius: "50%",
  animation: "spin 1s linear infinite",
  marginBottom: "20px",
};

const metricsContainerStyle = {
  display: "flex",
  flexDirection: "column",
  gap: "20px",
  padding: "20px",
};

const metricsCardStyle = {
  backgroundColor: "#fff",
  padding: "30px",
  borderRadius: "15px",
  boxShadow: "0px 4px 6px rgba(0,0,0,0.1)",
  textAlign: "center",
  width: "100%",
  maxWidth: "600px",
  margin: "0 auto",
};

const scoreCircleStyle = {
  width: "150px",
  height: "150px",
  borderRadius: "50%",
  backgroundColor: "#f5f5f5",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  margin: "20px auto",
  border: "3px solid #1a9766",
};

const scoreTextStyle = {
  fontSize: "48px",
  fontWeight: "bold",
  color: "#1a9766",
};

const healthStateStyle = {
  fontSize: "24px",
  color: "#333",
  marginTop: "10px",
};

const metricsGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
  gap: "20px",
  width: "100%",
  maxWidth: "1200px",
  margin: "0 auto",
};

const metricCardStyle = {
  backgroundColor: "#fff",
  padding: "20px",
  borderRadius: "15px",
  boxShadow: "0px 4px 6px rgba(0,0,0,0.1)",
  textAlign: "center",
};

const chartContainerStyle = {
  backgroundColor: "#fff",
  padding: "30px",
  borderRadius: "15px",
  boxShadow: "0px 4px 6px rgba(0,0,0,0.1)",
  width: "100%",
  maxWidth: "1200px",
  margin: "0 auto",
};
